"use client";
import React, { useMemo, useEffect } from "react";
import {
  Users, TrendingUp, ShoppingCart, IndianRupee,
  Star, UserCheck, UserX, BarChart3, ArrowUpRight,
  Award, Crown, Shield, User,
} from "lucide-react";
import {
  BarChart, Bar, AreaChart, Area, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer,
} from "recharts";
import { useCustomers } from "../context/CustomersContext";
import { useOrders }    from "../context/OrdersContext";

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmt = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");
const fmtShort = (n) => {
  if (n >= 1_00_000) return `₹${(n / 1_00_000).toFixed(1)}L`;
  if (n >= 1_000)    return `₹${(n / 1_000).toFixed(0)}k`;
  return `₹${n}`;
};

const TYPE_CONFIG = {
  vip:       { label:"VIP",       icon:Crown,    bg:"#FEF9C3", color:"#854D0E", border:"#FDE68A" },
  premium:   { label:"Premium",   icon:Star,     bg:"#F3E8FF", color:"#6B21A8", border:"#C4B5FD" },
  wholesale: { label:"Wholesale", icon:Shield,   bg:"#DBEAFE", color:"#1E40AF", border:"#93C5FD" },
  regular:   { label:"Regular",   icon:User,     bg:"#F3F4F6", color:"#4B5563", border:"#D1D5DB" },
};
const getTypeCfg = (t) => TYPE_CONFIG[(t||"regular").toLowerCase()] || TYPE_CONFIG.regular;

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const COLORS  = ["#0F766E","#14B8A6","#7C3AED","#F59E0B","#3B82F6","#EC4899"];

// Custom tooltip
function ChartTip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background:"rgba(255,255,255,0.97)", border:"1px solid #E5E7EB",
      borderRadius:12, padding:"10px 14px", boxShadow:"0 8px 24px rgba(0,0,0,0.12)", fontSize:12 }}>
      {label && <div style={{ fontWeight:700, color:"#6B7280", fontSize:10.5,
        textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:6 }}>{label}</div>}
      {payload.map((p, i) => (
        <div key={i} style={{ display:"flex", alignItems:"center", gap:8, marginBottom: i < payload.length-1 ? 4 : 0 }}>
          <span style={{ width:8, height:8, borderRadius:"50%", background:p.color||p.fill, flexShrink:0 }} />
          <span style={{ color:"#374151", fontWeight:500, flex:1 }}>{p.name}</span>
          <span style={{ fontWeight:800, color:"#111827", paddingLeft:8 }}>
            {typeof p.value === "number" && p.name?.toLowerCase().includes("rev")
              ? fmtShort(p.value) : p.value}
          </span>
        </div>
      ))}
    </div>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────
function KpiCard({ label, value, sub, icon: Icon, iconBg, iconColor, trend }) {
  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm hover:shadow-md transition-shadow flex flex-col gap-4">
      <div className="flex items-start justify-between">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ background: iconBg }}>
          <Icon className="w-5 h-5" style={{ color: iconColor }} />
        </div>
        {trend != null && (
          <span className={`flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full ${
            trend >= 0 ? "bg-green-100 text-green-700" : "bg-red-100 text-red-600"
          }`}>
            {trend >= 0 ? "↑" : "↓"} {Math.abs(trend)}%
          </span>
        )}
      </div>
      <div>
        <p className="text-2xl font-extrabold text-gray-900 tracking-tight leading-tight">{value}</p>
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mt-1">{label}</p>
        {sub && <p className="text-xs text-teal-600 font-semibold mt-1">{sub}</p>}
      </div>
    </div>
  );
}

function SectionCard({ title, icon: Icon, children, action }) {
  return (
    <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
        <div className="flex items-center gap-2.5">
          {Icon && (
            <div className="w-7 h-7 rounded-lg bg-teal-50 flex items-center justify-center">
              <Icon className="w-4 h-4 text-teal-600" />
            </div>
          )}
          <h3 className="text-sm font-bold text-gray-800">{title}</h3>
        </div>
        {action}
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function CustomersOverview() {
  const { customers, loading } = useCustomers();
  const { orders, fetchOrders } = useOrders();

  useEffect(() => { fetchOrders(); }, []); // eslint-disable-line

  // ── Per-customer stats from orders ──
  const customerStats = useMemo(() => {
    const stats = {};
    customers.forEach(c => {
      stats[c.id] = { totalOrders: 0, totalSpent: 0, lastOrderDate: null };
    });
    orders.forEach(o => {
      const id = o.customer?.id;
      if (!id || !stats[id]) return;
      stats[id].totalOrders += 1;
      stats[id].totalSpent  += Number(o.total || 0);
      if (!stats[id].lastOrderDate || new Date(o.date) > new Date(stats[id].lastOrderDate))
        stats[id].lastOrderDate = o.date;
    });
    return stats;
  }, [customers, orders]);

  // ── Enrich customers with stats ──
  const enriched = useMemo(() => customers.map(c => {
    const s = customerStats[c.id] || {};
    const spent  = s.totalSpent  || 0;
    const ords   = s.totalOrders || 0;
    let type = "regular";
    if (spent >= 100000 || ords >= 50) type = "vip";
    else if (spent >= 50000 || ords >= 20) type = "premium";
    else if (spent >= 20000 || ords >= 30) type = "wholesale";
    return { ...c, totalOrders: ords, totalSpent: spent,
      lastOrderDate: s.lastOrderDate, customerType: type };
  }), [customers, customerStats]);

  // ── KPIs ──
  const totalRevenue  = enriched.reduce((s, c) => s + c.totalSpent, 0);
  const totalOrders   = enriched.reduce((s, c) => s + c.totalOrders, 0);
  const activeCount   = enriched.filter(c => c.status === "active").length;
  const inactiveCount = enriched.length - activeCount;
  const avgSpend      = enriched.length ? Math.round(totalRevenue / enriched.length) : 0;
  const avgOrders     = enriched.length ? (totalOrders / enriched.length).toFixed(1) : 0;

  // ── Type breakdown ──
  const typeBreakdown = useMemo(() => {
    const counts = { vip:0, premium:0, wholesale:0, regular:0 };
    enriched.forEach(c => { counts[c.customerType] = (counts[c.customerType]||0) + 1; });
    return Object.entries(counts).map(([type, count]) => ({
      name: TYPE_CONFIG[type]?.label || type, value: count, color: COLORS[Object.keys(counts).indexOf(type)],
    })).filter(x => x.value > 0);
  }, [enriched]);

  // ── Status breakdown ──
  const statusData = [
    { name:"Active",   value: activeCount,   color:"#10B981" },
    { name:"Inactive", value: inactiveCount,  color:"#EF4444" },
  ].filter(x => x.value > 0);

  // ── Monthly registrations (from customer createdAt / registrationDate) ──
  const monthlyReg = useMemo(() => {
    const map = {};
    MONTHS.forEach((m, i) => { map[i] = { m, customers: 0, revenue: 0 }; });
    enriched.forEach(c => {
      const d = c.registrationDate || c.createdAt;
      if (!d) return;
      const mo = new Date(d).getMonth();
      map[mo].customers += 1;
      map[mo].revenue   += c.totalSpent;
    });
    return MONTHS.map((_, i) => map[i]);
  }, [enriched]);

  // ── Top customers by spend ──
  const topCustomers = useMemo(() =>
    [...enriched].sort((a,b) => b.totalSpent - a.totalSpent).slice(0, 8),
  [enriched]);

  // ── Recent customers ──
  const recentCustomers = useMemo(() =>
    [...enriched]
      .filter(c => c.registrationDate || c.createdAt)
      .sort((a,b) => new Date(b.registrationDate||b.createdAt) - new Date(a.registrationDate||a.createdAt))
      .slice(0, 6),
  [enriched]);

  if (loading && !customers.length) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-3 border-teal-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-6 bg-gray-50 min-h-screen">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-extrabold text-gray-900 tracking-tight">Customer Overview</h1>
          <p className="text-sm text-gray-500 mt-0.5">{enriched.length} total customers · updated now</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 bg-gradient-to-br from-teal-600 to-teal-800 rounded-xl flex items-center justify-center shadow">
            <Users className="w-5 h-5 text-white" />
          </div>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard label="Total Customers"  value={enriched.length.toLocaleString()}
          icon={Users}       iconBg="#F0FDFA" iconColor="#0F766E" />
        <KpiCard label="Active Customers" value={activeCount.toLocaleString()}
          icon={UserCheck}   iconBg="#DCFCE7" iconColor="#15803D"
          sub={`${enriched.length ? Math.round(activeCount/enriched.length*100) : 0}% of total`} />
        <KpiCard label="Total Revenue"    value={fmtShort(totalRevenue)}
          icon={IndianRupee} iconBg="#FEF9C3" iconColor="#854D0E"
          sub={`Avg ${fmtShort(avgSpend)} / customer`} />
        <KpiCard label="Total Orders"     value={totalOrders.toLocaleString()}
          icon={ShoppingCart} iconBg="#EFF6FF" iconColor="#1D4ED8"
          sub={`Avg ${avgOrders} orders / customer`} />
      </div>

      {/* Second KPI row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label:"VIP Customers",       value: enriched.filter(c=>c.customerType==="vip").length,       iconBg:"#FEF9C3", iconColor:"#854D0E", icon:Crown   },
          { label:"Premium Customers",   value: enriched.filter(c=>c.customerType==="premium").length,   iconBg:"#F3E8FF", iconColor:"#6B21A8", icon:Star    },
          { label:"Wholesale Customers", value: enriched.filter(c=>c.customerType==="wholesale").length, iconBg:"#DBEAFE", iconColor:"#1E40AF", icon:Shield  },
          { label:"Inactive Customers",  value: inactiveCount,                                           iconBg:"#FEE2E2", iconColor:"#991B1B", icon:UserX   },
        ].map(card => (
          <KpiCard key={card.label} {...card} value={card.value.toLocaleString()} />
        ))}
      </div>

      {/* Charts row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

        {/* Monthly registrations */}
        <div className="lg:col-span-2">
          <SectionCard title="Customer Growth & Revenue" icon={TrendingUp}>
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={monthlyReg}>
                <defs>
                  <linearGradient id="gCust" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#0F766E" stopOpacity={0.2} />
                    <stop offset="100%" stopColor="#0F766E" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gRev" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#7C3AED" stopOpacity={0.15} />
                    <stop offset="100%" stopColor="#7C3AED" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="2 8" stroke="rgba(0,0,0,0.05)" vertical={false} />
                <XAxis dataKey="m" tick={{ fontSize:11, fill:"#9CA3AF" }} axisLine={false} tickLine={false} />
                <YAxis yAxisId="c" tick={{ fontSize:11, fill:"#9CA3AF" }} axisLine={false} tickLine={false} width={28} />
                <YAxis yAxisId="r" orientation="right" tick={{ fontSize:11, fill:"#9CA3AF" }} axisLine={false} tickLine={false} width={44}
                  tickFormatter={v => fmtShort(v)} />
                <Tooltip content={<ChartTip />} />
                <Legend wrapperStyle={{ fontSize:11, paddingTop:8 }} />
                <Area yAxisId="c" type="monotone" dataKey="customers" stroke="#0F766E" strokeWidth={2.5}
                  fill="url(#gCust)" dot={false} name="New Customers" />
                <Area yAxisId="r" type="monotone" dataKey="revenue" stroke="#7C3AED" strokeWidth={2.5}
                  fill="url(#gRev)" dot={false} name="Revenue" />
              </AreaChart>
            </ResponsiveContainer>
          </SectionCard>
        </div>

        {/* Type breakdown pie */}
        <SectionCard title="Customer Types" icon={Award}>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={typeBreakdown} cx="50%" cy="44%"
                innerRadius={55} outerRadius={82} dataKey="value" paddingAngle={3} strokeWidth={0}>
                {typeBreakdown.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip formatter={(v) => [`${v} customers`]} />
              <Legend iconSize={8} wrapperStyle={{ fontSize:10.5 }} />
            </PieChart>
          </ResponsiveContainer>
        </SectionCard>
      </div>

      {/* Charts row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

        {/* Status bar chart */}
        <SectionCard title="Active vs Inactive" icon={BarChart3}>
          <div className="flex gap-4 mb-4">
            {statusData.map(s => (
              <div key={s.name} className="flex items-center gap-2 text-sm">
                <span className="w-3 h-3 rounded-full" style={{ background:s.color }} />
                <span className="font-medium text-gray-700">{s.name}</span>
                <span className="font-bold" style={{ color:s.color }}>{s.value}</span>
              </div>
            ))}
          </div>
          <div className="relative h-3 bg-gray-100 rounded-full overflow-hidden">
            {statusData[0] && (
              <div className="absolute left-0 top-0 h-full rounded-full transition-all"
                style={{ width:`${enriched.length ? (activeCount/enriched.length*100) : 0}%`, background:"#10B981" }} />
            )}
          </div>
          <p className="text-xs text-gray-400 mt-2 text-right">
            {enriched.length ? Math.round(activeCount/enriched.length*100) : 0}% active
          </p>
          <ResponsiveContainer width="100%" height={160} style={{ marginTop:16 }}>
            <BarChart data={typeBreakdown}>
              <CartesianGrid strokeDasharray="2 8" stroke="rgba(0,0,0,0.05)" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize:11, fill:"#9CA3AF" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize:11, fill:"#9CA3AF" }} axisLine={false} tickLine={false} width={28} />
              <Tooltip content={<ChartTip />} />
              <Bar dataKey="value" radius={[6,6,0,0]} name="Customers">
                {typeBreakdown.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </SectionCard>

        {/* Top customers */}
        <SectionCard title="Top Customers by Revenue" icon={Crown}>
          <div className="space-y-2">
            {topCustomers.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-8">No order data available yet</p>
            ) : topCustomers.map((c, i) => {
              const cfg = getTypeCfg(c.customerType);
              const TypeIcon = cfg.icon;
              const pct = topCustomers[0].totalSpent > 0 ? Math.round(c.totalSpent / topCustomers[0].totalSpent * 100) : 0;
              return (
                <div key={c.id} className="flex items-center gap-3 py-2">
                  {/* Rank */}
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                    i === 0 ? "bg-yellow-100 text-yellow-700" :
                    i === 1 ? "bg-gray-200 text-gray-600" :
                    i === 2 ? "bg-gray-100 text-orange-600" : "bg-gray-100 text-gray-400"
                  }`}>
                    {i + 1}
                  </span>

                  {/* Avatar */}
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-teal-500 to-teal-700 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                    {c.fullName?.charAt(0) || "?"}
                  </div>

                  {/* Info + bar */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-semibold text-gray-800 truncate">{c.fullName}</span>
                      <span className="text-sm font-bold text-teal-700 ml-2 flex-shrink-0">{fmtShort(c.totalSpent)}</span>
                    </div>
                    <div className="relative h-1.5 bg-gray-100 rounded-full overflow-hidden">
                      <div className="absolute left-0 top-0 h-full rounded-full bg-gradient-to-r from-teal-500 to-teal-300"
                        style={{ width:`${pct}%` }} />
                    </div>
                  </div>

                  {/* Type badge */}
                  <div className="w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ background: cfg.bg }}>
                    <TypeIcon className="w-3 h-3" style={{ color: cfg.color }} />
                  </div>
                </div>
              );
            })}
          </div>
        </SectionCard>
      </div>

      {/* Recent customers table */}
      {recentCustomers.length > 0 && (
        <SectionCard title="Recently Joined" icon={Users}
          action={
            <a href="/customers" className="text-xs text-teal-600 font-semibold flex items-center gap-1 hover:underline">
              View all <ArrowUpRight className="w-3 h-3" />
            </a>
          }>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-teal-700 text-white">
                  {["Customer","Email","Phone","Orders","Total Spent","Status","Type"].map((h,i) => (
                    <th key={h} className={`px-4 py-3 text-xs font-semibold uppercase tracking-wider ${i === 0 ? "text-left rounded-l" : i === 6 ? "text-left rounded-r" : "text-left"}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {recentCustomers.map((c, i) => {
                  const cfg = getTypeCfg(c.customerType);
                  const TypeIcon = cfg.icon;
                  return (
                    <tr key={c.id} className={`transition-colors hover:bg-teal-50/40 ${i % 2 === 0 ? "bg-white" : "bg-gray-50/50"}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2.5">
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-teal-500 to-teal-700 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                            {c.fullName?.charAt(0) || "?"}
                          </div>
                          <span className="font-semibold text-gray-800">{c.fullName}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-600">{c.email || "—"}</td>
                      <td className="px-4 py-3 text-gray-600">{c.phone || "—"}</td>
                      <td className="px-4 py-3 font-semibold text-gray-800">{c.totalOrders}</td>
                      <td className="px-4 py-3 font-bold text-teal-700">{fmtShort(c.totalSpent)}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                          c.status === "active" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-600"
                        }`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${c.status === "active" ? "bg-green-500" : "bg-red-400"}`} />
                          {c.status === "active" ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold"
                          style={{ background:cfg.bg, color:cfg.color }}>
                          <TypeIcon className="w-3 h-3" />
                          {cfg.label}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </SectionCard>
      )}

      {/* Empty state */}
      {customers.length === 0 && !loading && (
        <div className="text-center py-20">
          <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Users className="w-8 h-8 text-gray-300" />
          </div>
          <h3 className="text-lg font-semibold text-gray-500 mb-1">No customers yet</h3>
          <p className="text-sm text-gray-400">Customer data will appear here once you have customers.</p>
        </div>
      )}
    </div>
  );
}
